import { boolean, discriminatedUnion, enum as enumFactory, iso, literal, strictObject, string, url } from "../../lazy-zod.js";
import { openMeldRequestContextSchema } from "../daemon/profile-identity.js";
import { centerAgentToolSpeakerSchema } from "./center-agent-speaker.js";
//#region src/schemas/http/organization-join-link.ts
const ORGANIZATION_JOIN_LINK_WEB_PATH = "/join-organization";
const ORGANIZATION_JOIN_LINK_TOKEN_PREFIX = "joinlink_";
const ORGANIZATION_JOIN_LINK_ACCEPT_RATE_LIMITED_ERROR_CODE = "organization.join_link.accept.rate_limited";
const organizationJoinLinkTokenSchema = string().regex(/^joinlink_[0-9a-f]{64}$/);
const organizationJoinLinkManagementQuerySchema = strictObject({
	organizationId: string().trim().min(1).max(255),
	spaceId: string().trim().min(1).max(255).optional(),
	mode: enumFactory(["direct", "request"]).optional(),
	responseVersion: literal("3").optional()
});
const organizationJoinLinkSchema = strictObject({
	id: string().min(1),
	organizationId: string().min(1),
	token: organizationJoinLinkTokenSchema,
	url: url(),
	role: literal("member"),
	createdBy: string().min(1),
	createdAt: iso.datetime(),
	mode: enumFactory(["direct", "request"]).optional(),
	expiresAt: iso.datetime().nullable().optional(),
	spaceId: string().min(1).nullable().optional()
});
const getOrganizationJoinLinkResponseSchema = strictObject({
	ok: literal(true),
	joinLink: organizationJoinLinkSchema.nullable()
});
const CENTER_AGENT_ORGANIZATION_INVITE_LINK_GENERATION_GUIDANCE = {
	requiredRole: "member",
	settingsLocation: "Settings → Workspace → General",
	settingsSection: "Invite link"
};
const centerAgentGetOrganizationInviteLinkToolRpcInputSchema = strictObject({ context: openMeldRequestContextSchema });
const centerAgentOrganizationInviteLinkGenerationGuidanceSchema = strictObject({
	requiredRole: literal(CENTER_AGENT_ORGANIZATION_INVITE_LINK_GENERATION_GUIDANCE.requiredRole),
	settingsLocation: literal(CENTER_AGENT_ORGANIZATION_INVITE_LINK_GENERATION_GUIDANCE.settingsLocation),
	settingsSection: literal(CENTER_AGENT_ORGANIZATION_INVITE_LINK_GENERATION_GUIDANCE.settingsSection)
});
const centerAgentGetOrganizationInviteLinkResponseSchema = discriminatedUnion("active", [strictObject({
	active: literal(true),
	organizationName: string().trim().min(1),
	url: url()
}), strictObject({
	active: literal(false),
	generationGuidance: centerAgentOrganizationInviteLinkGenerationGuidanceSchema
})]);
const organizationJoinLinkEnsureErrorCodeSchema = enumFactory([
	"organization.join_link.forbidden",
	"organization.join_link.inactive",
	"organization.join_link.invalid_input",
	"organization.metadata_invalid",
	"organization.personal_no_members"
]);
const ensureOrganizationJoinLinkOnBehalfOfRpcInputSchema = strictObject({
	actorUserId: string().trim().min(1).max(255),
	organizationId: string().trim().min(1).max(255)
});
const ensureOrganizationJoinLinkOnBehalfOfRpcResponseSchema = discriminatedUnion("ok", [strictObject({
	joinLink: organizationJoinLinkSchema,
	ok: literal(true),
	organizationName: string().trim().min(1)
}), strictObject({
	code: organizationJoinLinkEnsureErrorCodeSchema,
	message: string().trim().min(1),
	ok: literal(false)
})]);
const centerAgentCreateOrganizationInviteLinkToolRpcInputSchema = strictObject({
	context: openMeldRequestContextSchema,
	speakerMember: centerAgentToolSpeakerSchema
});
const centerAgentCreateOrganizationInviteLinkResponseSchema = discriminatedUnion("ok", [strictObject({
	ok: literal(true),
	organizationName: string().trim().min(1),
	url: url()
}), strictObject({
	error: strictObject({
		code: organizationJoinLinkEnsureErrorCodeSchema,
		message: string().trim().min(1)
	}),
	ok: literal(false)
})]);
const revokeOrganizationJoinLinkResponseSchema = strictObject({
	ok: literal(true),
	revoked: boolean()
});
const previewOrganizationJoinLinkQuerySchema = strictObject({
	token: organizationJoinLinkTokenSchema,
	responseVersion: enumFactory(["2", "3"]).optional()
});
const previewOrganizationJoinLinkResponseSchema = discriminatedUnion("active", [strictObject({
	ok: literal(true),
	active: literal(true),
	organizationName: string().min(1),
	organizationSlug: string().min(1),
	mode: enumFactory(["direct", "request"]).optional(),
	expiresAt: iso.datetime().nullable().optional(),
	spaceId: string().min(1).nullable().optional()
}), strictObject({
	ok: literal(true),
	active: literal(false)
})]);
const acceptOrganizationJoinLinkBodySchema = strictObject({ token: organizationJoinLinkTokenSchema });
const acceptOrganizationJoinLinkResponseSchema = strictObject({
	ok: literal(true),
	organizationId: string().min(1),
	organizationName: string().min(1),
	organizationSlug: string().min(1),
	role: enumFactory([
		"owner",
		"admin",
		"member"
	]),
	alreadyMember: boolean()
});
const organizationJoinLinkErrorCodeSchema = enumFactory([
	"auth.invalid_origin",
	ORGANIZATION_JOIN_LINK_ACCEPT_RATE_LIMITED_ERROR_CODE,
	"organization.join_link.invalid_input",
	"organization.join_link.authentication_required",
	"organization.join_link.approval_required",
	"organization.join_link.forbidden",
	"organization.join_link.email_verification_required",
	"organization.join_link.inactive",
	"organization.personal_no_members",
	"organization.metadata_invalid"
]);
const organizationJoinLinkErrorResponseSchema = strictObject({
	ok: literal(false),
	code: organizationJoinLinkErrorCodeSchema,
	message: string().min(1)
});
//#endregion
export { CENTER_AGENT_ORGANIZATION_INVITE_LINK_GENERATION_GUIDANCE, ORGANIZATION_JOIN_LINK_ACCEPT_RATE_LIMITED_ERROR_CODE, ORGANIZATION_JOIN_LINK_TOKEN_PREFIX, ORGANIZATION_JOIN_LINK_WEB_PATH, acceptOrganizationJoinLinkBodySchema, acceptOrganizationJoinLinkResponseSchema, centerAgentCreateOrganizationInviteLinkResponseSchema, centerAgentCreateOrganizationInviteLinkToolRpcInputSchema, centerAgentGetOrganizationInviteLinkResponseSchema, centerAgentGetOrganizationInviteLinkToolRpcInputSchema, centerAgentOrganizationInviteLinkGenerationGuidanceSchema, ensureOrganizationJoinLinkOnBehalfOfRpcInputSchema, ensureOrganizationJoinLinkOnBehalfOfRpcResponseSchema, getOrganizationJoinLinkResponseSchema, organizationJoinLinkEnsureErrorCodeSchema, organizationJoinLinkErrorCodeSchema, organizationJoinLinkErrorResponseSchema, organizationJoinLinkManagementQuerySchema, organizationJoinLinkSchema, organizationJoinLinkTokenSchema, previewOrganizationJoinLinkQuerySchema, previewOrganizationJoinLinkResponseSchema, revokeOrganizationJoinLinkResponseSchema };

//# sourceMappingURL=organization-join-link.js.map